import { CardContent } from '@mui/material'
import { Spacer, Item } from '../../styles'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { formatDate } from '../formatDate'
import { StyledView, StyledTypography } from './styles'

/**
 * A component that displays the user's personal or company information
 *
 * @param {object} contract - Contains the user's contract data
 * @param {string} contract.contractNumber - The user's contract number identifier
 * @param {string} contract.fullName - Full name of the user or company
 * @param {string} contract.salutation - Salutation (Mr/Mrs/Firma) - determines if individual or company view is shown
 * @param {object} contract.address - The user's primary address
 * @param {string} contract.address.street - Street name
 * @param {string} contract.address.houseNumber - House/building number
 * @param {string} contract.address.postalCode - Postal/zip code
 * @param {string} contract.address.city - City name
 * @param {object} contract.billingAddress - The user's billing address (if different)
 * @param {string} contract.billingAddress.name - Name of billing address owner
 * @param {string} contract.billingAddress.street - Street name for billing
 * @param {string} contract.billingAddress.houseNumber - House/building number for billing
 * @param {string} contract.billingAddress.postalCode - Postal/zip code for billing
 * @param {string} contract.billingAddress.city - City name for billing
 * @param {string} contract.emailAddress - User's email address
 * @param {string} contract.phoneNumber - User's phone number
 * @param {string} contract.dateOfBirth - Birth date (individual users only)
 * @param {string} contract.registeredCourt - Registered court (company only)
 * @param {string} contract.registrationNumber - Registration number (company only)
 */
function UserInformation({ contract }) {
  const { t } = useTranslation()
  const isCompany = contract?.salutation === 'COMPANY'
  let billingAddressName = ''
  let billingAddressStreet = ''
  let billingAddressHouseNumber = ''
  let billingAddressPostalCode = ''
  let billingAddressCity = ''
  if (contract?.isBillingAddressSameAsAddress) {
    if (contract?.salutation === 'COMPANY') {
      billingAddressName = contract?.companyName
    } else {
      billingAddressName = contract?.firstName + ' ' + contract?.lastName
    }
    billingAddressStreet = contract?.address?.street
    billingAddressHouseNumber = contract?.address?.houseNumber
    billingAddressPostalCode = contract?.address?.postalCode
    billingAddressCity = contract?.address?.city
  } else {
    billingAddressName = contract?.billingAddress?.name
    billingAddressStreet = contract?.billingAddress?.street
    billingAddressHouseNumber = contract?.billingAddress?.houseNumber
    billingAddressPostalCode = contract?.billingAddress?.postalCode
    billingAddressCity = contract?.billingAddress?.city
  }

  return (
    <>
      <StyledView>
        <CardContent>
          <StyledTypography fontWeight="bold" variant="h4" gutterBottom>
            {t('LBLUserInfo')}
          </StyledTypography>
          <Spacer mb={4} />
          <StyledTypography variant="body2" component="div" gutterBottom>
            {/* Contract Number field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLCustomerNum')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.contractNumber}
              </StyledTypography>
            </Item>

            {/* Name field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('Name')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {`${t(contract?.salutation)} ${contract?.firstName || ''} ${
                  contract?.lastName || ''
                }${contract?.companyName || ''}`.trim()}
              </StyledTypography>
            </Item>

            {/* Address field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLAddress')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.address?.street} {contract?.address?.houseNumber}{' '}
                <br />
                {contract?.address?.postalCode} {contract?.address?.city}
              </StyledTypography>
            </Item>

            {/* Billing Owner field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLBillingAddressOwner')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {billingAddressName}
              </StyledTypography>
            </Item>

            {/* Billing Address field */}
            {contract?.billingAddress && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLBillingAddress')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {billingAddressStreet} {billingAddressHouseNumber} <br />
                    {billingAddressPostalCode} {billingAddressCity}
                  </StyledTypography>
                </Item>
              </>
            )}

            {/* Email field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {'E-Mail'}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.email}
              </StyledTypography>
            </Item>

            {/* Phone Number field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLHandy')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.phone}
              </StyledTypography>
            </Item>
            <div style={{ height: '5px' }}></div>

            {/* Fields specific to individuals (non-company) */}
            {!isCompany && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLBirthDate')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {formatDate(contract?.dateOfBirth)}
                  </StyledTypography>
                </Item>
              </>
            )}

            {/* Fields specific to companies */}
            {isCompany && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLRegisterCourt')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {contract?.registeredCourt}
                  </StyledTypography>
                </Item>
              </>
            )}

            {isCompany && (
              <>
                <Item>
                  <StyledTypography fontWeight="fontWeightMedium">
                    {t('LBLRegisterNumber')}
                  </StyledTypography>
                  <StyledTypography
                    fontWeight="fontWeightRegular"
                    align="right"
                  >
                    {contract?.registrationNumber}
                  </StyledTypography>
                </Item>
              </>
            )}
          </StyledTypography>
        </CardContent>
      </StyledView>
    </>
  )
}

export default UserInformation
